import { replaceEqualDeep } from "@tanstack/react-query";

// Structural sharing keeps the cached reference when a write is deep-equal to what is cached, so observers that
// track `data` don't re-render. That is right for a refetch that returns the same thing, and wrong for a manual
// write: much of the app changes cached objects in place before handing them to setQueryData, which makes the
// old and new values deep-equal even though the user just changed something. A manual write is therefore copied
// and remembered here, and the structural-sharing hook lets it through as a new reference. Fetched results never
// enter the set, so they keep full structural sharing.
const manualWrites = new WeakSet<object>();

// Only plain objects and arrays are copied: they are the only values structural sharing would ever swap for the
// cached reference. Anything else (a Date, a class instance) is passed through, as replaceEqualDeep does. The two
// checks mirror query-core's own, which its typings don't export.
function isPlainObject(value: unknown): value is object {
    if (value === null || typeof value !== "object") {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

export function markManualWrite<T>(next: T): T {
    if (Array.isArray(next)) {
        const copy = [...next] as T & object;
        manualWrites.add(copy);
        return copy;
    }

    if (isPlainObject(next)) {
        const copy = { ...next } as T & object;
        manualWrites.add(copy);
        return copy;
    }

    return next;
}

export function shareUnlessManualWrite(oldData: unknown, newData: unknown): unknown {
    if (typeof newData === "object" && newData !== null && manualWrites.has(newData)) {
        return newData;
    }

    return replaceEqualDeep(oldData, newData);
}
